/**
 * A list of billing rules with on/off switches, edit and remove, and "Add a
 * rule in plain English": the assistant reads the sentence into a typed rule
 * and the admin checks it before it's saved. Used for firm rules and for the
 * rules read from a client's guidelines.
 */
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useState, type FC } from "react";
import { toast } from "sonner";

import { AgentRobot } from "@/components/brand";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

import { GuidelineAPI } from "../api/timeApi";
import type { BillingRule } from "../types";
import { RuleEditor } from "./RuleEditor";
import {
  ruleTitle,
  severityText,
  toggled,
  withoutRule,
  withRule
} from "./ruleModel";

type Props = {
  rules: BillingRule[];
  canEdit: boolean;
  onChange: (rules: BillingRule[], message: string) => void;
  empty?: string;
  testId?: string;
};

const RuleRow: FC<{
  rule: BillingRule;
  canEdit: boolean;
  onToggle: (on: boolean) => void;
  onEdit: () => void;
  onRemove: () => void;
}> = ({ rule, canEdit, onToggle, onEdit, onRemove }) => (
  <div
    className="flex min-h-11 items-center gap-3 border-b border-line-2 px-3.5 py-2 last:border-b-0"
    data-testid="billing-rule"
    title={rule.quote}
  >
    <Switch
      aria-label={`Check "${rule.label}"`}
      checked={rule.enabled}
      disabled={!canEdit}
      onCheckedChange={onToggle}
    />
    <span
      className={cn(
        "flex min-w-0 flex-1 flex-col",
        !rule.enabled && "opacity-60"
      )}
    >
      <span className="truncate text-base font-medium">{ruleTitle(rule)}</span>
      <span className="truncate text-sm text-fg-3">
        {rule.summary}
        {rule.quote ? ` · "${rule.quote}"` : ""}
      </span>
    </span>
    {rule.check === "model" ? (
      <Badge variant="secondary" className="gap-1 font-normal">
        <AgentRobot state="static" className="size-3" /> Casey checks
      </Badge>
    ) : null}
    <Badge
      variant="secondary"
      className={cn(
        "font-normal",
        rule.severity === "error"
          ? "border-destructive/30 bg-destructive/10 text-danger"
          : "border-warn-line bg-warn-soft text-warn"
      )}
    >
      {severityText(rule.severity)}
    </Badge>
    {canEdit ? (
      <span className="flex items-center">
        <Button
          size="icon-sm"
          variant="ghost"
          aria-label={`Edit "${rule.label}"`}
          onClick={onEdit}
        >
          <Pencil />
        </Button>
        <Button
          size="icon-sm"
          variant="ghost"
          aria-label={`Remove "${rule.label}"`}
          onClick={onRemove}
        >
          <Trash2 />
        </Button>
      </span>
    ) : null}
  </div>
);

const AddRule: FC<{
  onDraft: (rule: BillingRule, guessed: boolean) => void;
}> = ({ onDraft }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      const { guessed, ...draft } = await GuidelineAPI.parseRule(text.trim());
      onDraft(draft, guessed);
      setText("");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Couldn't read that rule"
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      className="flex items-center gap-2 border-t border-line-2 px-3.5 py-2.5"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <Input
        aria-label="Add a rule in plain English"
        placeholder="Add a rule in plain English, e.g. Associates no more than $300 an hour"
        className="h-8 max-w-115 text-base"
        value={text}
        onChange={(e) => setText(e.currentTarget.value)}
      />
      <Button
        type="submit"
        size="sm"
        variant="secondary"
        disabled={!text.trim() || busy}
      >
        {busy ? <Spinner /> : <Plus />} Add rule
      </Button>
    </form>
  );
};

export const RuleList: FC<Props> = ({
  rules,
  canEdit,
  onChange,
  empty = "No rules yet.",
  testId
}) => {
  const [editing, setEditing] = useState<{
    rule: BillingRule;
    isNew: boolean;
    guessed: boolean;
  } | null>(null);
  return (
    <div
      className="shrink-0 overflow-hidden rounded-xl border border-line-2"
      data-testid={testId}
    >
      {rules.length ? null : (
        <div className="px-3.5 py-3 text-base text-fg-3">{empty}</div>
      )}
      {rules.map((rule) => (
        <RuleRow
          key={rule.id}
          rule={rule}
          canEdit={canEdit}
          onToggle={(on) =>
            onChange(
              toggled(rules, rule.id, on),
              `${on ? "Turned on" : "Turned off"} "${rule.label}"`
            )
          }
          onEdit={() => setEditing({ rule, isNew: false, guessed: false })}
          onRemove={() =>
            onChange(withoutRule(rules, rule.id), `Removed "${rule.label}"`)
          }
        />
      ))}
      {canEdit ? (
        <AddRule
          onDraft={(rule, guessed) =>
            setEditing({ rule, isNew: true, guessed })
          }
        />
      ) : null}
      <RuleEditor
        rule={editing?.rule ?? null}
        title={editing?.isNew ? "Add a rule" : "Edit rule"}
        guessed={editing?.guessed}
        saveLabel={editing?.isNew ? "Add rule" : "Save rule"}
        onClose={() => setEditing(null)}
        onSave={(rule) => {
          onChange(
            withRule(rules, rule),
            editing?.isNew ? `Added "${rule.label}"` : `Saved "${rule.label}"`
          );
          setEditing(null);
        }}
      />
    </div>
  );
};
